import { html, css, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { dom, library } from '@fortawesome/fontawesome-svg-core';
import { faExchange, faEdit, faTags } from '@fortawesome/pro-solid-svg-icons';
import { DataPoint, TierDetailSettings } from '@aptitude/shared-vanilla';

import { svgWithJs, variables } from '../variables';
import '../box/apt-box';
import '../labeled-icon/apt-labeled-icon';
import '../data-summary/apt-data-summary';
import '../target-chart/apt-target-chart';

// Note: Your element must have a hyphen in the name (for example, "hello-world"). It's a requirement
// so that our components don't collide with future additions to HTML.
@customElement('apt-offer-summary')
export class OfferSummary extends LitElement {
  @property({ type: Object })
  compact = { value: false };

  @property()
  data: Array<DataPoint> = [];

  @property({ type: Object })
  filtered = { value: false };

  @property({ type: String })
  offerName = '';

  // Really not in love with the naming for these attributes.
  @property({ type: Number })
  crossed = 0;

  @property({ type: Object })
  editPermission = { value: true };

  @property({ type: Number })
  totalCrossed = 0;

  @property({ type: Number })
  priced = 0;

  @property({ type: String })
  target = '';

  @property({ type: String })
  targetFull = '';

  @property({ type: Number })
  totalPriced = 0;

  @property({ type: Number })
  tierNumber = 0;

  // This is needed to feed through to the modal.
  @property()
  tiers: Array<any> = [];

  @property({ type: String })
  value = '';

  @property({ type: Object })
  displayAttainability = { value: true };

  @property({ type: Object })
  displayOptimized = { value: true };

  @property({ type: Object })
  disableTierButton = { value: false };

  @property({ type: String })
  operator = '';

  @property({ type: Object })
  tierDetailSettings = {} as TierDetailSettings;

  COLOR_RANGE = [0.9, 0.97, 1];

  static override get styles() {
    return [
      variables,
      svgWithJs,
      css`
        :host {
          display: block;
          box-sizing: border-box;
        }

        .offer-summary {
          min-width: 230px;
          min-height: 120px;
          max-width: 230px;
          max-height: 120px;
          font-family: var(--font-family-sans-serif);
        }

        .offer-summary.compact {
          min-width: 460px;
          min-height: 60px;
          max-width: 460px;
          max-height: 60px;
        }

        .inner-box {
          display: flex;
          flex-direction: column;
          height: 110px;

          text-align: left;
          padding: 5px;
          max-width: 230px;
        }

        .compact .inner-box {
          flex-direction: row;
          height: 50px;
          max-width: 460px;
        }

        .top-area {
          max-width: 230px;
          display: flex;
          justify-content: space-between;

          flex-grow: 1;
        }

        .left-top-area {
          width: 70%;
        }

        .compact .inner-box .top-area {
          margin-right: 2.5px;
        }

        .compact .inner-box .bottom-area {
          margin-left: 2.5px;
        }

        .left-top-area .offer-name {
          font-size: 14px;
          font-weight: bold;

          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
        }

        .left-top-area button {
          margin-top: 5px;
          display: block;

          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
        }

        .right-top-area {
          display: flex;
          justify-content: center;
          align-items: flex-start;
        }

        .labeled-icon {
          font-size: 13px;
          width: 26px;
        }

        .right-top-area .labeled-icon span.counts {
          font-size: 12px;
        }

        .bottom-area {
          display: flex;
          align-items: center;
          flex-grow: 1;
        }

        .bottom-area .right-bottom-area {
          overflow: hidden;
        }

        .bottom-area .target-chart {
          margin-right: 5px;
        }

        .bottom-area .value {
          font-size: large;
          font-weight: 500;
          cursor: default;
        }

        .bottom-area .target {
          font-size: 12px;
          color: #545454;
          overflow: hidden;
          text-overflow: ellipsis;
          font-weight: bold;
          cursor: default;
        }

        .tier-button {
          margin-bottom: 0;
          font-weight: normal;
          text-align: center;
          vertical-align: middle;
          cursor: pointer;
          background-image: none;
          border: 1px solid rgba(0, 0, 0, 0);
          color: #333;
          background-color: #fff;
          border-color: #ccc;
          padding: 1px 5px;
          font-size: 12px;
          line-height: 1.5;
          border-radius: 3px;
          margin-bottom: 10px;
        }

        .tier-button-disabled {
          background-color: #e6e6e6;
          background-position: 0 -15px;
          cursor: not-allowed;
        }

        .tier-button:hover {
          transition: background-position 0.1s linear 0s;
          background-color: #e6e6e6;
          background-position: 0 -15px;
        }
      `,
    ];
  }

  private handleClick(e: Event) {
    const event = new CustomEvent<TierEvent>('onTierClick', {
      bubbles: true,
      detail: {
        tiers: this.tiers,
        tierNumber: this.tierNumber,
        editPermission: this.editPermission?.value ?? false,
        offerName: this.offerName,
        displayAttainability: this.displayAttainability?.value ?? false,
        displayOptimized: this.displayOptimized?.value ?? false,
        tierDetailSettings: this.tierDetailSettings,
      },
    });
    this.dispatchEvent(event);
  }

  getTierButton() {
    if (this.disableTierButton?.value === true) {
      return html`
        <button disabled class="tier-button tier-button-disabled">
          Tier ${this.tierNumber} <i class="fa fa-solid fa-edit"></i>
        </button>
      `;
    } else {
      return html`
        <button class="tier-button" @click=${this.handleClick}>
          Tier ${this.tierNumber} <i class="fa fa-solid fa-edit"></i>
        </button>
      `;
    }
  }

  override render() {
    return html`
      <apt-box class="offer-summary ${this.isCompact()}">
        <div class="inner-box">
          <div class="top-area">
            <div class="left-top-area">
              <div class="offer-name">${this.offerName}</div>
              ${this.getTierButton()}
            </div>
            <div class="right-top-area">
              <apt-labeled-icon
                class=".labeled-icon"
                color="${this.labeledIconColor(
      this.crossed,
      this.totalCrossed,
    )}"
                icon="fa fa-exchange"
                enableTooltip
                position="bottom"
              >
                <apt-data-summary
                  slot="data-summary"
                  color="${this.labeledIconColor(
      this.crossed,
      this.totalCrossed,
    )}"
                  ?filtered="${this.filtered?.value}"
                  icon="fa-exchange"
                  label="Incumbent Crossed"
                  .partial="${this.crossed}"
                  .total="${this.totalCrossed}"
                ></apt-data-summary>
              </apt-labeled-icon>
              <apt-labeled-icon
                class=".labeled-icon"
                color="${this.labeledIconColor(this.priced, this.totalPriced)}"
                icon="fa fa-tags"
                enableTooltip
              >
                <apt-data-summary
                  slot="data-summary"
                  color="${this.labeledIconColor(
      this.priced,
      this.totalPriced,
    )}"
                  ?filtered="${this.filtered?.value}"
                  icon="fa-exchange"
                  label="Products Priced"
                  .partial="${this.priced}"
                  .total="${this.totalPriced}"
                ></apt-data-summary>
              </apt-labeled-icon>
            </div>
          </div>
          <div class="bottom-area">
            <apt-target-chart
              class="target-chart"
              .data="${this.data}"
              .operator="${this.operator}"
              .colorRange="${this.COLOR_RANGE}"
            ></apt-target-chart>
            <div class="right-bottom-area">
              <div class="value">${this.value}</div>
              <div class="target" title="${this.targetFull}">
                ${this.target}
              </div>
            </div>
          </div>
        </div>
      </apt-box>
    `;
  }

  isCompact() {
    return this.compact?.value === true ? 'compact' : '';
  }

  iconColor(numerator: number, denominator: number) {
    if (denominator > 0) {
      const fraction = numerator / denominator;

      if (fraction < 0.5) {
        return 'danger';
      } else if (fraction < 0.8) {
        return 'warning';
      } else {
        return 'primary';
      }
    } else {
      return 'danger';
    }
  }

  labeledIconColor(numerator: number, denominator: number): string {
    return this.iconColor(numerator, denominator);
  }

  protected override firstUpdated(): void {
    library.add(faEdit);
    library.add(faExchange);
    library.add(faTags);

    // @ts-ignore The dom.watch() TS definition indicates no parameters for this. However, when
    // you actually dig into the code, you see that it looks at the arguments after it gets into
    // the code and tries to pull some out if there aren't any (it even supplies an empty object
    // for options if you don't supply one yourself).
    //
    // Note: We have to wait for the first update for the shadow DOM to even be created.
    dom.watch({
      autoReplaceSvgRoot: this.shadowRoot,
      observeMutationsRoot: this.shadowRoot,
    });
  }
}

export interface TierEvent {
  tiers: Array<any>;
  tierNumber: number;
  editPermission: boolean;
  offerName: string;
  displayAttainability: boolean;
  displayOptimized: boolean;
  tierDetailSettings: TierDetailSettings;
}

export interface BooleanAttribute {
  value: boolean;
}

declare global {
  interface HTMLElementTagNameMap {
    'apt-offer-summary': OfferSummary;
  }
}
